import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { 
  Plus, 
  ArrowLeft,
  Calendar,
  User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import RichTextEditor from '../components/RichTextEditor';

const API = '/api';

function stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

function BlogPostCard({ post, index }) {
  const plainContent = stripHtml(post.content);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link to={`/blog/${post.id}`} data-testid={`blog-post-${post.id}`}>
        <Card className="bg-white border-[#E2E8F0] hover:border-[#16A34A]/40 hover:shadow-md transition-all group cursor-pointer">
          <CardContent className="p-6 lg:p-8">
            <h2 className="text-xl lg:text-2xl font-bold text-[#052E16] group-hover:text-[#16A34A] transition-colors mb-4 font-['Manrope']">
              {post.title}
            </h2>
            <p className="text-[#57534E] line-clamp-3 mb-4">
              {plainContent.substring(0, 200)}
            </p>
            <div className="flex items-center gap-4 text-sm text-[#57534E]">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.author_name}
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(post.created_at).toLocaleDateString('cs-CZ')}
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  );
}

function BlogPostDetail({ postId }) {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const { data } = await axios.get(`${API}/blog/${postId}`, { withCredentials: true });
        setPost(data);
      } catch (error) {
        console.error('Error fetching post:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [postId]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-12 bg-[#F0FDF4] rounded-xl w-3/4" />
        <div className="h-4 bg-[#F0FDF4] rounded w-1/4" />
        <div className="h-64 bg-[#F0FDF4] rounded-xl" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="text-center py-12 text-[#57534E]">
        Článek nebyl nalezen
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="blog-post-detail"
    >
      <Link 
        to="/blog" 
        className="inline-flex items-center gap-2 text-[#57534E] hover:text-[#16A34A] mb-6 transition-colors"
        data-testid="back-to-blog"
      >
        <ArrowLeft className="w-4 h-4" />
        Zpět na blog
      </Link>
      
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-8 lg:p-12">
          <h1 className="text-3xl lg:text-4xl font-bold text-[#052E16] mb-6 font-['Manrope']">
            {post.title}
          </h1>
          
          <div className="flex items-center gap-6 text-sm text-[#57534E] mb-8 pb-8 border-b border-[#E2E8F0]">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-[#16A34A]" />
              {post.author_name}
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#F59E0B]" />
              {new Date(post.created_at).toLocaleDateString('cs-CZ', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </div>
          </div>
          
          <div className="prose prose-lg max-w-none text-[#1C1917]" data-testid="blog-post-content">
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default function BlogPage() {
  const { postId } = useParams();
  const { isAdmin, canWriteBlog } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchPosts = async () => {
    try {
      const { data } = await axios.get(`${API}/blog`, { withCredentials: true });
      setPosts(data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!postId) {
      fetchPosts();
    }
  }, [postId]);

  const handleCreatePost = async () => {
    if (!newPost.title.trim() || !newPost.content.trim()) {
      toast.error('Vyplňte všechna pole');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/blog`, newPost, { withCredentials: true });
      toast.success('Článek byl vytvořen');
      setDialogOpen(false);
      setNewPost({ title: '', content: '' });
      fetchPosts();
    } catch (error) {
      toast.error('Nepodařilo se vytvořit článek');
    } finally {
      setSubmitting(false);
    }
  };

  if (postId) {
    return (
      <Layout>
        <BlogPostDetail postId={postId} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="blog-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-[#052E16] font-['Manrope']">
              Blog
            </h1>
            <p className="text-[#57534E] mt-2">
              Novinky a informace o firmě
            </p>
          </div>
          
          {canWriteBlog && (
            <Button
              onClick={() => setDialogOpen(true)}
              data-testid="create-post-btn"
              className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold rounded-xl shadow-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nový článek
            </Button>
          )}
        </div>

        {/* Posts List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 rounded-2xl bg-[#F0FDF4] animate-pulse" />
            ))}
          </div>
        ) : posts.length > 0 ? (
          <div className="space-y-4">
            {posts.map((post, index) => (
              <BlogPostCard key={post.id} post={post} index={index} />
            ))}
          </div>
        ) : (
          <Card className="bg-white border-[#E2E8F0]">
            <CardContent className="py-12 text-center">
              <p className="text-[#57534E]">Zatím zde nejsou žádné články</p>
            </CardContent>
          </Card>
        )}

        {/* Create Post Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white border-[#E2E8F0] text-[#1C1917] max-w-3xl">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Nový článek</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nadpis</Label>
                <Input
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  placeholder="Zadejte nadpis..."
                  data-testid="post-title-input"
                  className="bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
                />
              </div>
              <div className="space-y-2">
                <Label>Obsah</Label>
                <RichTextEditor
                  content={newPost.content}
                  onChange={(html) => setNewPost({ ...newPost, content: html })}
                  placeholder="Napište obsah článku..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-[#E2E8F0] text-[#57534E] hover:bg-[#F8FAF8]"
              >
                Zrušit
              </Button>
              <Button
                onClick={handleCreatePost}
                disabled={submitting}
                data-testid="submit-post-btn"
                className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold"
              >
                {submitting ? 'Ukládám...' : 'Vytvořit'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
